import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { CAPABILITY } from '@trustero/trustero-api-web/lib/authz/permissions_pb'
import { AdvancedSearchOptions } from 'src/Utils/search/search.types'
import { parseSearchQuery } from 'src/Utils/search/search.helpers'
import { useGetURLParams } from 'src/Utils/dashboards/dashboards.hooks'
import { CloseIcon, Divider } from '../../FloatingMenu/FloatingMenu.styles'
import { SimpleIconButton } from '../../Buttons/SimpleIconButton'
import { PrimaryButton, SecondaryButton } from '../../Buttons'
import {
  AdvancedSearchButtonRow,
  AdvancedSearchFormBody,
  AdvancedSearchFormContainer,
  ExactMatchField,
  ExcludeAllField,
  IncludeAllField,
  IncludeAnyField,
  TitleContainer,
} from './AdvancedSearch.styles'

export const AdvancedSearchForm = ({
  onClose,
  query,
  onSearch,
  show,
  buttonRef,
}: {
  onClose: () => void
  query?: string
  onSearch: (
    query: string,
    advancedSearch?: AdvancedSearchOptions,
    isContentSearchActive?: boolean,
  ) => void
  show: boolean
  buttonRef: React.RefObject<HTMLDivElement>
}): JSX.Element => {
  const searchParams = useGetURLParams()
  const [advancedSearch, setAdvancedSearch] = useState<
    AdvancedSearchOptions | undefined
  >(undefined)
  const formRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (show) {
      const searchOptions = parseSearchQuery(query || '')
      setAdvancedSearch({
        exactMatch: searchOptions.getExactMatchStrsList().join(' '),
        includeAny: searchOptions.getIncludeSomeStrsList().join(' '),
        includeAll: searchOptions.getIncludeAllStrsList().join(' '),
        excludeAll: searchOptions.getExcludedStrsList().join(' '),
      })
    }
  }, [show, query])

  const handleClose = useCallback(() => {
    setAdvancedSearch(undefined)
    onClose()
  }, [onClose])

  const isContentSearchActive = useMemo(() => {
    return searchParams['contentSearch'] === 'true'
  }, [searchParams])

  // close and reset form on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        event.target instanceof Node &&
        !formRef.current?.contains(event.target) &&
        !buttonRef.current?.contains(event.target)
      ) {
        handleClose()
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [handleClose, formRef, buttonRef])

  const onSubmit = useCallback(() => {
    if (!advancedSearch) {
      return
    }
    onSearch('', advancedSearch, isContentSearchActive)
    handleClose()
  }, [advancedSearch, onSearch, handleClose, isContentSearchActive])

  return (
    <AdvancedSearchFormContainer ref={formRef}>
      <TitleContainer>
        Advanced Search
        <SimpleIconButton onClick={handleClose}>
          <CloseIcon />
        </SimpleIconButton>
      </TitleContainer>
      <Divider />
      <AdvancedSearchFormBody>
        <ExactMatchField
          name="exactMatch"
          initVal={advancedSearch?.exactMatch || ''}
          placeholder="Enter keywords"
          label="Exact Match"
          form="advanced-search-form"
          autoFocus={true}
          setFormData={
            setAdvancedSearch as unknown as Dispatch<
              SetStateAction<{ [key: string]: string }>
            >
          }
        />
        <IncludeAnyField
          label="Include Any"
          name="includeAny"
          initVal={advancedSearch?.includeAny || ''}
          placeholder="Enter keywords"
          form="advanced-search-form"
          setFormData={
            setAdvancedSearch as unknown as Dispatch<
              SetStateAction<{ [key: string]: string }>
            >
          }
        />
        <IncludeAllField
          label="Include All"
          name="includeAll"
          initVal={advancedSearch?.includeAll || ''}
          placeholder="Enter keywords"
          form="advanced-search-form"
          setFormData={
            setAdvancedSearch as unknown as Dispatch<
              SetStateAction<{ [key: string]: string }>
            >
          }
        />
        <ExcludeAllField
          label="Exclude All"
          name="excludeAll"
          initVal={advancedSearch?.excludeAll || ''}
          placeholder="Enter keywords"
          form="advanced-search-form"
          setFormData={
            setAdvancedSearch as unknown as Dispatch<
              SetStateAction<{ [key: string]: string }>
            >
          }
        />
        <AdvancedSearchButtonRow>
          <SecondaryButton
            text="Reset"
            onClick={() =>
              setAdvancedSearch({
                exactMatch: '',
                includeAny: '',
                includeAll: '',
                excludeAll: '',
              })
            }
            requiredCapabilities={[CAPABILITY.CAN_USE_SEARCH]}
          />
          <PrimaryButton
            text="Search"
            onClick={onSubmit}
            requiredCapabilities={[CAPABILITY.CAN_USE_SEARCH]}
          />
        </AdvancedSearchButtonRow>
      </AdvancedSearchFormBody>
    </AdvancedSearchFormContainer>
  )
}
